import { DealRegistryPageListDueDiligenceRecordsQuery, DueDiligenceRecordsListSortBy, Security_Status } from '@/__generated__/graphql';
import ActionDropdown from '@/components/basicComponents/actionDropdown';
import { Tooltip } from '@/components/basicComponents/tooltip';
import { Loader } from '@/components/loaders/loader';
import NoResults from '@/components/table/noResults';
import RedirectTitleCell from '@/components/table/redirectTitleCell';
import {
  TableBodyWrapper,
  TableFixedCell,
  TableRowCell,
  TableScrollableCellsWrapper,
  TableWithHorizontalScroll,
  TableWithHorizontalScrollRowWrapper,
  TableWithHorizontalScrollWrapper,
  TextCell
} from '@/components/table/styles';
import TableHeader from '@/components/table/tableHeader';
import { TableColumns } from '@/components/table/types';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { themeVar } from '@/styles/theme';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { NavigateOptions } from 'react-router-dom';
import styled from 'styled-components';
import { actionDropdownItems } from '../utils';

const getAIRunValue = (completedAt: Date) => {
  if (completedAt) {
    return format(new Date(completedAt), 'LLL d, yyyy');
  } else {
    return 'In Progress';
  }
};

export const cellValue = (
  row: DealRegistryPageListDueDiligenceRecordsQuery['listDueDiligenceRecords']['records'][number],
  column: TableColumns,
  t: (key: string) => string
) => {
  switch (column.cellKey) {
    case 'createdAt':
      return <TextCell>{format(new Date(row.createdAt), 'LLL d, yyyy')}</TextCell>;
    case 'assetClass':
      return <TextCell>{row.investment.assetClass && row.investment.assetClass}</TextCell>;
    case 'tier':
      return (
        <TextCell>
          {row.investment.securityStatus === 'LEAD'
            ? `${row.activeTier?.funnel.latestFunnel.name} - Not Started`
            : `${row.activeTier?.funnel.latestFunnel.name} - ${row.activeTier?.name}`}
        </TextCell>
      );
    case 'targetIRR':
      return <TextCell>{row.investment?.targetIRR}</TextCell>;
    case 'targetYield':
      return <TextCell>{row.investment?.targetYield}</TextCell>;
    case 'strategy':
      return row.investment?.subAssetClasses?.length > 1 ? (
        <Tooltip title={row.investment.subAssetClasses.toString().replace(/,/g, ', ')}>
          <TextCell>Multiple ({row.investment.subAssetClasses.length})</TextCell>
        </Tooltip>
      ) : (
        <TextCell>{row.investment?.subAssetClasses && row.investment?.subAssetClasses[0]}</TextCell>
      );
    case 'tags':
      return row.investment.tags?.length > 1 ? (
        <Tooltip title={row.investment.tags.toString().replace(/,/g, ', ')}>
          <TextCell>Multiple ({row.investment.tags.length})</TextCell>
        </Tooltip>
      ) : row.investment.tags?.length ? (
        <TextCell>row.investment?.tags[0]</TextCell>
      ) : (
        <></>
      );
    case 'status':
      return <TextCell>{t(`enums:SECURITY_STATUS.${row.investment.securityStatus}`)}</TextCell>;
    case 'ai_suggestions':
      return row.dealAssistantAIRuns && row.dealAssistantAIRuns.length > 0 ? (
        <TextCell>{getAIRunValue(row.dealAssistantAIRuns[0].completedAt)}</TextCell>
      ) : (
        <TextCell>Never Run</TextCell>
      );
    default:
      return <TextCell>{row[column.cellKey as keyof typeof row]}</TextCell>;
  }
};

interface DealRegistryTableProps {
  columnsList: TableColumns[];
  dealRegistryRows: DealRegistryPageListDueDiligenceRecordsQuery['listDueDiligenceRecords']['records'];
  loading: boolean;
  flashLoading: boolean;
  currentRow: DealRegistryPageListDueDiligenceRecordsQuery['listDueDiligenceRecords']['records'][number] | null;
  openPage: (path: string, options?: NavigateOptions) => void;
  openModalWindow: (row: DealRegistryPageListDueDiligenceRecordsQuery['listDueDiligenceRecords']['records'][number], type: string) => void;
  handleUpdateInvestmentStatus: (
    row: DealRegistryPageListDueDiligenceRecordsQuery['listDueDiligenceRecords']['records'][number],
    status: Security_Status
  ) => void;
}

export const DealRegistryTable = ({
  columnsList,
  dealRegistryRows,
  loading,
  flashLoading,
  currentRow,
  openPage,
  openModalWindow,
  handleUpdateInvestmentStatus
}: DealRegistryTableProps) => {
  const { user } = useAuth();
  const { isTablet } = useResponsive();
  const { t } = useTranslation('enums');
  const { searchParams, onChangeSort } = useSearchParamsManager();

  const key = (searchParams.get('key') as DueDiligenceRecordsListSortBy) || DueDiligenceRecordsListSortBy.CreatedDate;
  const asc = searchParams.get('asc') === 'true' || false;

  // 5 is the gap (spacing) between table cells
  const totalTableWidth = columnsList.reduce((width, column) => (column.isHidden ? width : width + column.width + 5), 0);

  return (
    <TableWithHorizontalScrollWrapper isTablet={isTablet}>
      <TableWithHorizontalScroll totalWidth={`${totalTableWidth}px`}>
        <TableHeader columns={columnsList} savedSetSort={onChangeSort} savedSort={{ key, asc }} refetch={() => {}} withHorizontalScroll />
        {loading ? (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        ) : (
          <TableBodyWrapper>
            {!dealRegistryRows.length ? (
              <NoResults />
            ) : (
              <>
                {dealRegistryRows.map((row) => (
                  <TableWithHorizontalScrollRowWrapper key={row.id} loading={flashLoading && row.id === currentRow?.id ? 1 : 0}>
                    <TableFixedCell id="fixedCell" width={columnsList[0].width} alignItems="center">
                      <CellWithActionDropdown>
                        <div>
                          <RedirectTitleCell onClick={() => openPage(`/dueDiligence/${row.id}?backToTitle=Deal Registry`)}>
                            {row.investment.name}
                          </RedirectTitleCell>
                          {row.manager && <ManagerWrapper>{row.manager}</ManagerWrapper>}
                        </div>
                        <ActionDropdown options={actionDropdownItems(row, user, openPage, openModalWindow, handleUpdateInvestmentStatus)} />
                      </CellWithActionDropdown>
                    </TableFixedCell>
                    <TableScrollableCellsWrapper alignItems="center">
                      {columnsList
                        .filter((column) => !column.isFixed)
                        .map(
                          (column) =>
                            !column.isHidden && (
                              <TableRowCell key={column.keySort} width={column.width} withHorizontalScroll>
                                {cellValue(row, column, t)}
                              </TableRowCell>
                            )
                        )}
                    </TableScrollableCellsWrapper>
                  </TableWithHorizontalScrollRowWrapper>
                ))}
              </>
            )}
          </TableBodyWrapper>
        )}
      </TableWithHorizontalScroll>
    </TableWithHorizontalScrollWrapper>
  );
};

const LoaderWrapper = styled.div`
  position: absolute;
  left: 50%;
  margin-top: 80px;
`;

const CellWithActionDropdown = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const ManagerWrapper = styled.div`
  font-weight: 400;
  font-size: 13px;
  color: ${themeVar('font.base')};
`;
